import { ROUTES } from '@/router/paths';

export type ZoneId = 'analytics' | 'tasks';

export interface NavItem {
  path: string;
  icon: string;
  label: string;
  rememberLocation?: boolean;
  children?: NavItem[];
}

export interface Zone {
  id: ZoneId;
  label: string;
  icon: string;
  items: NavItem[];
}

export const ZONES: Zone[] = [
  {
    id: 'analytics',
    label: 'Аналитика',
    icon: 'ri-bar-chart-2-line',
    items: [
      { path: ROUTES.analytics.dynamics, icon: 'ri-line-chart-line', label: 'Динамика' },
      {
        path: ROUTES.analytics.wordstat,
        icon: 'ri-bar-chart-grouped-line',
        label: 'Wordstat',
      },
      {
        path: ROUTES.analytics.serpset,
        icon: 'ri-search-eye-line',
        label: 'Выдача',
        rememberLocation: true,
      },
    ],
  },
  {
    id: 'tasks',
    label: 'Задание',
    icon: 'ri-settings-3-line',
    items: [
      { path: ROUTES.tasks.overview, icon: 'ri-pulse-line', label: 'Обзор' },
      {
        path: ROUTES.tasks.oneTime,
        icon: 'ri-flask-line',
        label: 'Эксперименты',
        children: [
          { path: ROUTES.tasks.recurring, icon: 'ri-time-line', label: 'Регулярные' },
          { path: ROUTES.tasks.oneTime, icon: 'ri-flask-line', label: 'Единоразовые' },
        ],
      },
      { path: ROUTES.tasks.wordstat, icon: 'ri-bar-chart-grouped-line', label: 'Wordstat' },
      { path: ROUTES.tasks.targets, icon: 'ri-focus-3-line', label: 'Цели' },
      { path: ROUTES.tasks.baskets, icon: 'ri-archive-line', label: 'Корзины' },
      { path: ROUTES.tasks.connections, icon: 'ri-key-2-line', label: 'Подключения' },
    ],
  },
];

const LAST_VISITED_PATH_KEY_PREFIX = 'rranking:last-visited-path:';
const LAST_VISITED_ITEM_PATH_KEY_PREFIX = 'rranking:last-visited-item-path:';

function matchesPath(pathname: string, path: string): boolean {
  return path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
}

export function findZoneFromPath(pathname: string): ZoneId | null {
  for (const zone of ZONES) {
    for (const item of zone.items) {
      if (matchesPath(pathname, item.path)) return zone.id;
      if (item.children) {
        for (const child of item.children) {
          if (matchesPath(pathname, child.path)) return zone.id;
        }
      }
    }
  }
  return null;
}

export function getZoneFromPath(pathname: string): ZoneId {
  return findZoneFromPath(pathname) ?? 'analytics';
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function belongsToZone(path: string, zoneId: ZoneId): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const pathname = path.split(/[?#]/, 1)[0];
  return findZoneFromPath(pathname) === zoneId;
}

function findRememberedItem(pathname: string): NavItem | null {
  for (const zone of ZONES) {
    for (const item of zone.items) {
      const candidates = item.children ?? [item];
      const match = candidates.find(
        (candidate) => candidate.rememberLocation && matchesPath(pathname, candidate.path),
      );
      if (match) return match;
    }
  }
  return null;
}

function belongsToItem(path: string, itemPath: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const pathname = path.split(/[?#]/, 1)[0];
  return matchesPath(pathname, itemPath);
}

export function rememberZonePath(zoneId: ZoneId, path: string): void {
  if (!belongsToZone(path, zoneId)) return;

  try {
    getSessionStorage()?.setItem(`${LAST_VISITED_PATH_KEY_PREFIX}${zoneId}`, path);
  } catch {
    // Navigation must continue to work when browser storage is unavailable.
  }
}

export function getRememberedZonePath(zoneId: ZoneId): string | null {
  try {
    const path = getSessionStorage()?.getItem(`${LAST_VISITED_PATH_KEY_PREFIX}${zoneId}`) ?? null;
    return path && belongsToZone(path, zoneId) ? path : null;
  } catch {
    return null;
  }
}

export function rememberNavItemPath(path: string): void {
  if (!path.startsWith('/') || path.startsWith('//')) return;
  const pathname = path.split(/[?#]/, 1)[0];
  const item = findRememberedItem(pathname);
  if (!item) return;

  try {
    getSessionStorage()?.setItem(`${LAST_VISITED_ITEM_PATH_KEY_PREFIX}${item.path}`, path);
  } catch {
    // Navigation must continue to work when browser storage is unavailable.
  }
}

export function getRememberedNavItemPath(itemPath: string): string | null {
  const item = findRememberedItem(itemPath);
  if (!item || item.path !== itemPath) return null;

  try {
    const path =
      getSessionStorage()?.getItem(`${LAST_VISITED_ITEM_PATH_KEY_PREFIX}${item.path}`) ?? null;
    return path && belongsToItem(path, item.path) ? path : null;
  } catch {
    return null;
  }
}

export function getDefaultZonePath(zoneId: ZoneId): string {
  const zone = ZONES.find(({ id }) => id === zoneId);
  if (!zone?.items[0]) return ROUTES.analytics.dynamics;
  return zone.items[0].path;
}
